import type { AdminIdentity, ContractSigner, CustomerEditableField, MarketingRight, OrderForm, OrderFormActivity, OrderFormActivityType, OrderFormDiscountScope, OrderFormDiscountType, OrderFormLine, OrderFormSigning, RenewalOption, WorkspaceAsset, WorkspaceMember } from '../types'
import { appliedDiscount, cents } from './proposal-math'
import { orderFormLineAmount, sumOrderFormLines, termAssumedFor } from './order-form-line'
import { unitMoney } from './format'
import { invoiceScheduleChoiceFromText } from './order-form-billing'

export const DEFAULT_CSA_URL = '/api/documents/csa'
export const DEFAULT_CSA_TITLE = 'Cloud Service Agreement'

export const ORDER_FORM_DEFAULTS = {
  // Rod, 2026-10-16: billing starts at signature unless the form says otherwise.
  billingStart: 'At Signature',
  invoiceSchedule: 'Annually',
  paymentTerms: 'Due Upon Receipt',
  paymentMethods: 'ACH',
  marketingRights: ['quote', 'logo', 'case_study'] as MarketingRight[],
  licenseDescription: 'Enables a member to access all AI tools and other features of the Prentus platform for the entirety of their job search. Licenses are only used when a member finishes onboarding and has a Jobseeking status.',
} as const

export const MARKETING_RIGHTS: Array<{ key: MarketingRight; label: string }> = [
  { key: 'quote', label: 'Quote' },
  { key: 'logo', label: 'Logo' },
  { key: 'case_study', label: 'Case Study' },
]

/** "Quote, Logo and Case Study", "Quote and Logo", "Logo", or "None". */
export function marketingRightsText(rights: MarketingRight[]): string {
  const labels = MARKETING_RIGHTS.filter((right) => rights.includes(right.key)).map((right) => right.label)
  if (labels.length === 0) return 'None'
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/** A line's amount for the whole term of this form, at the net price. */
export function lineTotal(line: OrderFormLine, form: Pick<OrderForm, 'termStart' | 'termEnd'>): number | null {
  return orderFormLineAmount(line, contractLengthMonths(form.termStart, form.termEnd))
}

/** True when a monthly, quarterly or flat yearly line needs the term and the form has no term dates yet, so one year is assumed. */
export function orderFormTermAssumed(form: Pick<OrderForm, 'lines' | 'termStart' | 'termEnd'>): boolean {
  return termAssumedFor(form.lines, contractLengthMonths(form.termStart, form.termEnd))
}

export interface OrderFormTotals {
  /** Where the discount comes off: the license subtotal, or licenses plus add-ons. */
  discountScope: OrderFormDiscountScope
  licenses: number
  discount: number
  /** Licenses net of the discount when the scope is 'licenses'; the plain license subtotal otherwise. */
  licenseTotal: number
  addOns: number
  /** Licenses plus add-ons, before any discount. */
  subscriptionSubtotal: number
  recurring: number
  /** One-time fees net of credits. Negative when the credits are larger. */
  oneTime: number
  /** One-time credits as a positive amount (a buyout credit, for example). */
  credits: number
  total: number
}

export const DISCOUNT_SCOPE_LABEL: Record<OrderFormDiscountScope, string> = {
  licenses: 'Licenses Only',
  subscription: 'Licenses and Add-Ons',
}

type DiscountFields = Pick<OrderForm, 'lines' | 'discountEnabled' | 'discountAmount'> & { discountScope?: OrderFormDiscountScope; discountType?: OrderFormDiscountType; discountPercent?: number | null; termStart?: string | null; termEnd?: string | null }

/** The order discount's type. A form saved before percent discounts has none and reads as dollars, so its totals do not change. */
export function orderDiscountType(form: { discountType?: OrderFormDiscountType | null }): OrderFormDiscountType {
  return form.discountType === 'percent' ? 'percent' : 'amount'
}

/** The percent off, clamped to 0 to 100, or null when none is entered. */
function percentOff(form: DiscountFields): number | null {
  const value = form.discountPercent
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return Math.min(100, Math.max(0, value))
}

/** Removes floating-point noise from a percent (0.1 x 100) for display. */
function snapPercent(value: number): number {
  return Math.round(value * 1e6) / 1e6
}

/**
 * A credit is a one-time line with a negative unit price, for example a
 * buyout credit. Only one-time lines may be negative; licenses and add-ons
 * stay non-negative (the server schema enforces it).
 */
export function isCreditLine(line: Pick<OrderFormLine, 'category' | 'unitPrice'>): boolean {
  return line.category === 'one_time' && line.unitPrice !== null && line.unitPrice < 0
}

/**
 * Dock's layout, which this mirrors: by default the discount applies to the
 * license block, each line carries its own line discount (applied first, so
 * every subtotal here is already net of line discounts), and the
 * Totals block shows the recurring figure before the grand total. With the
 * 'subscription' scope (the Full Platform discount, 10% off licenses and
 * modules) the discount comes off licenses plus add-ons instead, and the
 * license block keeps its full subtotal.
 */
export function orderFormTotals(form: DiscountFields): OrderFormTotals {
  const discountScope: OrderFormDiscountScope = form.discountScope === 'subscription' ? 'subscription' : 'licenses'
  const months = contractLengthMonths(form.termStart ?? null, form.termEnd ?? null)
  const sum = (lines: OrderFormLine[]) => sumOrderFormLines(lines, months)
  const licenses = sum(form.lines.filter((line) => line.category === 'license'))
  const addOns = sum(form.lines.filter((line) => line.category === 'add_on'))
  const subscriptionSubtotal = cents(licenses + addOns)
  const oneTime = sum(form.lines.filter((line) => line.category === 'one_time'))
  const credits = Math.abs(sum(form.lines.filter(isCreditLine)))
  const base = discountScope === 'subscription' ? subscriptionSubtotal : licenses
  // A percent discount is that share of the base the scope picks; a dollar discount is the typed amount. Both are capped at the base.
  const percent = percentOff(form)
  const requested = orderDiscountType(form) === 'percent' ? (percent === null ? null : cents(base * percent / 100)) : form.discountAmount
  const discount = appliedDiscount(base, form.discountEnabled, requested)
  const licenseTotal = discountScope === 'subscription' ? licenses : cents(licenses - discount)
  const recurring = cents(subscriptionSubtotal - discount)
  return { discountScope, licenses, discount, licenseTotal, addOns, subscriptionSubtotal, recurring, oneTime, credits, total: cents(recurring + oneTime) }
}

/** The subtotal the discount comes off: licenses, or licenses plus add-ons. */
export function discountBase(totals: OrderFormTotals): number {
  return totals.discountScope === 'subscription' ? totals.subscriptionSubtotal : totals.licenses
}

/**
 * "10%" for a percent discount. For a dollar discount, "12.5%" when the
 * amount is a clean share of the subtotal it comes off, otherwise null.
 */
export function discountPercentLabel(form: DiscountFields): string | null {
  const totals = orderFormTotals(form)
  const base = discountBase(totals)
  if (!totals.discount || !base) return null
  if (orderDiscountType(form) === 'percent') {
    const typed = percentOff(form)
    return typed === null ? null : `${snapPercent(typed)}%`
  }
  const percent = (totals.discount / base) * 100
  const rounded = Math.round(percent * 10) / 10
  if (Math.abs(percent - rounded) > 0.01) return null
  return `${rounded}%`
}

/**
 * The discount row label on the document, the PDF and the editor totals:
 * "Full Platform Discount (10%)". The percent is left out when the label
 * already states it ("10% off licenses" stays as typed).
 */
export function discountRowLabel(form: DiscountFields & Pick<OrderForm, 'discountLabel'>): string {
  const label = form.discountLabel.trim() || 'Discount'
  const percent = discountPercentLabel(form)
  if (!percent) return label
  const digits = percent.slice(0, -1).replace('.', '\\.')
  const stated = new RegExp(`(^|[^\\d.])${digits}\\s*%`).test(label)
  return stated ? label : `${label} (${percent})`
}

/** A warning when the typed discount is larger than the subtotal it comes off (or a percent above 100), so the total shows less than intended. */
export function discountExceedsBase(form: DiscountFields): boolean {
  if (!form.discountEnabled) return false
  if (orderDiscountType(form) === 'percent') return typeof form.discountPercent === 'number' && form.discountPercent > 100
  if (form.discountAmount === null || !Number.isFinite(form.discountAmount)) return false
  return form.discountAmount > discountBase(orderFormTotals(form))
}

/** Whole months between the term dates, counting the end date as inclusive. */
export function contractLengthMonths(termStart: string | null, termEnd: string | null): number | null {
  if (!termStart || !termEnd) return null
  const start = new Date(`${termStart}T12:00:00Z`)
  const end = new Date(`${termEnd}T12:00:00Z`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return null
  end.setUTCDate(end.getUTCDate() + 1)
  const months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth())
  return months + (end.getUTCDate() >= start.getUTCDate() ? 0 : -1)
}

/**
 * Recurring lines are quantities for the whole term (pooled licenses), so a
 * recurring total is the amount for the term, not a yearly rate. The suffix
 * only says "/year" when the term is exactly one year. A known longer or
 * shorter term reads "for the 3-year term" or "for the 18-month term". With
 * no term dates yet it is empty: the plain amount, with no period. The
 * editor, the HTML document and the PDF all use this one helper.
 */
export function recurringSuffix(form: Pick<OrderForm, 'termStart' | 'termEnd'>): string {
  const months = contractLengthMonths(form.termStart, form.termEnd)
  if (months === null) return ''
  if (months === 12) return '/year'
  if (months % 12 === 0) return ` for the ${months / 12}-year term`
  return ` for the ${months}-month term`
}

export function makeOrderFormNumber(now = new Date()): string {
  const stamp = now.toISOString().slice(0, 10).replace(/-/g, '')
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const bytes = new Uint8Array(4)
  crypto.getRandomValues(bytes)
  const suffix = Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('')
  return `OF-${stamp}-${suffix}`
}

export function blankOrderForm(accountName: string, now = new Date()): OrderForm {
  const iso = now.toISOString()
  const validUntil = new Date(now.getTime() + 30 * 86_400_000).toISOString().slice(0, 10)
  return {
    id: crypto.randomUUID(),
    number: makeOrderFormNumber(now),
    title: `Prentus <> ${accountName}`,
    status: 'draft',
    createdAt: iso,
    updatedAt: iso,
    sourceOptionId: null,
    validUntil,
    customer: { legalName: accountName, website: '', address: '', contactName: '', contactTitle: '', contactEmail: '', billingEmail: '', billingName: '', poNumber: '' },
    prentusContact: { name: '', title: '', email: '' },
    preparedBy: { name: '', title: '', email: '' },
    termStart: null,
    termEnd: null,
    billingStart: ORDER_FORM_DEFAULTS.billingStart,
    billingStartChoice: 'at_signature',
    billingStartDate: null,
    invoiceSchedule: ORDER_FORM_DEFAULTS.invoiceSchedule,
    invoiceScheduleChoice: 'annually',
    paymentTerms: ORDER_FORM_DEFAULTS.paymentTerms,
    paymentMethods: ORDER_FORM_DEFAULTS.paymentMethods,
    marketingRights: ORDER_FORM_DEFAULTS.marketingRights,
    lines: [],
    discountEnabled: false,
    discountLabel: '',
    discountAmount: null,
    discountType: 'percent',
    discountPercent: null,
    discountScope: 'licenses',
    overageRate: null,
    licensesPooled: false,
    additionalTerms: '',
    includeDefaultCsa: true,
    shareSignedCopy: true,
    signing: null,
    signedAt: null,
  }
}

/** The line fields every new line starts with: per unit, no line discount, Annually (One-Time on a one-time line). */
export function lineDefaults(category: OrderFormLine['category']): Pick<OrderFormLine, 'pricing' | 'billingFrequency' | 'discountType' | 'discountValue'> {
  return { pricing: 'per_unit', billingFrequency: category === 'one_time' ? 'one_time' : 'annually', discountType: null, discountValue: null }
}

export function newOrderFormLine(category: OrderFormLine['category'], licenseQuantity: number | null = null): OrderFormLine {
  if (category === 'one_time') return { id: crypto.randomUUID(), category, name: 'Implementation', description: 'One-time setup, configuration, and training. Non-refundable.', quantity: 1, unitPrice: null, unitLabel: null, ...lineDefaults(category) }
  if (category === 'license') return { id: crypto.randomUUID(), category, name: 'Pro AI Jobseeker Licenses', description: ORDER_FORM_DEFAULTS.licenseDescription, quantity: licenseQuantity, unitPrice: null, unitLabel: null, ...lineDefaults(category) }
  return { id: crypto.randomUUID(), category, name: '', description: '', quantity: licenseQuantity, unitPrice: null, unitLabel: '/license', ...lineDefaults(category) }
}

/** Converts a renewal proposal option into a draft order form with the same lines. */
export function orderFormFromOption(accountName: string, option: RenewalOption, headline: string, now = new Date()): OrderForm {
  const base = blankOrderForm(accountName, now)
  const lines: OrderFormLine[] = []
  if (option.licenseQuantity !== null || option.licenseUnitPrice !== null) {
    lines.push({ id: crypto.randomUUID(), category: 'license', name: option.licenseLabel || 'Active Jobseeker Licenses', description: ORDER_FORM_DEFAULTS.licenseDescription, quantity: option.licenseQuantity, unitPrice: option.licenseUnitPrice, unitLabel: null, ...lineDefaults('license') })
  }
  for (const addOn of option.addOns) {
    lines.push({ id: crypto.randomUUID(), category: 'add_on', name: addOn.name, description: addOn.description, quantity: addOn.quantity, unitPrice: addOn.unitPrice, unitLabel: addOn.unitLabel, ...lineDefaults('add_on') })
  }
  const schedule = option.billingFrequency && /annual/i.test(option.billingFrequency) ? 'Annually' : option.billingFrequency || base.invoiceSchedule
  return {
    ...base,
    title: `Prentus <> ${accountName}`,
    sourceOptionId: option.id,
    termStart: option.termStart,
    termEnd: option.termEnd,
    invoiceSchedule: schedule,
    invoiceScheduleChoice: invoiceScheduleChoiceFromText(schedule),
    lines,
    discountEnabled: option.discountEnabled,
    discountLabel: option.discountLabel || (headline ? `${headline} discount` : ''),
    discountAmount: option.discountAmount,
    // A renewal option prices its discount in dollars.
    discountType: 'amount',
    discountPercent: null,
    discountScope: 'licenses',
  }
}

export const DOCUSEAL_APP_ORIGIN = 'https://docuseal.com'

/** The embedded DocuSeal form for one signer. Used only as an embed source, never shown as a link. */
export function docusealEmbedUrl(slug: string): string {
  return `${DOCUSEAL_APP_ORIGIN}/s/${slug}`
}

/** The customer's link to their Order Form tab. Emails, Copy link and the admin panel all use this, never a DocuSeal URL. */
export function orderFormPortalUrl(origin: string, workspaceSlug: string): string {
  return `${origin.replace(/\/$/, '')}/${workspaceSlug}#order-form`
}

/** The order form's Review & send page in the admin, where the Prentus signer signs. */
export function orderFormAdminSignUrl(origin: string, workspaceId: string, formId: string): string {
  return `${origin.replace(/\/$/, '')}/admin/workspaces/${workspaceId}/order-forms/${formId}/edit#send`
}

export type OrderFormStage = 'draft' | 'awaiting_prentus' | 'awaiting_customer' | 'signed' | 'declined' | 'void'

/**
 * Where a form stands, from its stored status and the signer states. The
 * stored status stays draft / sent / signed / void; "sent" splits by whose
 * turn it is. Signers are stored in signing order, so the first pending
 * signer is the one the form waits on (Prentus on new forms, the customer on
 * forms sent before Prentus signed first).
 */
export function orderFormStage(form: { status: OrderForm['status']; signing: Pick<OrderFormSigning, 'signers'> | null }): OrderFormStage {
  if (form.status !== 'sent') return form.status
  const signers = form.signing?.signers || []
  if (signers.some((signer) => signer.status === 'declined')) return 'declined'
  const next = signers.find((signer) => signer.status !== 'completed')
  return next?.role === 'Prentus' ? 'awaiting_prentus' : 'awaiting_customer'
}

/** Admin wording for the list, the Review & send panel and the Contracts page. */
export const ORDER_FORM_STAGE_LABEL: Record<OrderFormStage, string> = {
  draft: 'Draft',
  awaiting_prentus: 'Awaiting Prentus Signature',
  awaiting_customer: 'Awaiting Customer Signature',
  signed: 'Signed',
  declined: 'Declined',
  void: 'Void',
}

/** Customer wording: they never see "Awaiting Prentus Signature". */
export const ORDER_FORM_CUSTOMER_STAGE_LABEL: Record<OrderFormStage, string> = {
  draft: 'Draft',
  awaiting_prentus: 'Being Prepared',
  awaiting_customer: 'Awaiting Signature',
  signed: 'Signed',
  declined: 'Declined',
  void: 'Void',
}

/** StatusBadge tones (src/components/ui/status-badge): neutral, info, pending, success, danger. */
export const ORDER_FORM_STAGE_BADGE: Record<OrderFormStage, 'neutral' | 'info' | 'pending' | 'success' | 'danger'> = {
  draft: 'neutral',
  awaiting_prentus: 'pending',
  awaiting_customer: 'pending',
  signed: 'success',
  declined: 'danger',
  void: 'neutral',
}

export const REMINDER_INTERVAL_MS = 60 * 60 * 1000

/** Why "Send a Reminder" cannot go out now, or null when it can. One reminder an hour, counted from the last request. */
export function reminderBlocked(form: Pick<OrderForm, 'status' | 'signing'>, now = Date.now()): string | null {
  if (form.status !== 'sent' || !form.signing) return 'This order form is not out for signature.'
  if (!customerTurn(form.signing)) return 'The customer cannot sign yet. Prentus signs first.'
  const last = form.signing.remindedAt || form.signing.customerRequestedAt
  if (last && now - Date.parse(last) < REMINDER_INTERVAL_MS) return 'A reminder went out less than an hour ago. Try again later.'
  return null
}

export function orderFormStatusLabel(form: Parameters<typeof orderFormStage>[0]): string {
  return ORDER_FORM_STAGE_LABEL[orderFormStage(form)]
}

/** True once the customer may sign: every signer before them has signed. */
export function customerTurn(signing: Pick<OrderFormSigning, 'signers'> | null): boolean {
  if (!signing) return false
  const index = signing.signers.findIndex((signer) => signer.role === 'Customer')
  return index >= 0 && signing.signers[index].status === 'pending' && signing.signers.slice(0, index).every((signer) => signer.status === 'completed')
}

/** The customer's signing embed, when the viewer is the customer signer and it is their turn (the DTO only carries their slug then). */
export function viewerSigningUrl(form: Pick<OrderForm, 'status' | 'signing'>): string | null {
  if (form.status !== 'sent' || !form.signing || !customerTurn(form.signing)) return null
  const mine = form.signing.signers.find((signer) => signer.role === 'Customer' && signer.slug && signer.status === 'pending')
  return mine?.slug ? docusealEmbedUrl(mine.slug) : null
}

/** Sent and signed forms are the only ones a customer may see. */
export function customerVisibleOrderForm(form: Pick<OrderForm, 'status'>): boolean {
  return form.status === 'sent' || form.status === 'signed'
}

export interface OrderFormAttachment {
  id: string
  title: string
  url: string | null
  kind: 'default_csa' | 'upload'
  assetId: string | null
}

/**
 * The agreements that travel with this order form: the standard CSA on file
 * when the form keeps it, then every uploaded agreement tagged with the form.
 */
export function orderFormAttachments(form: Pick<OrderForm, 'id' | 'includeDefaultCsa'>, assets: WorkspaceAsset[]): OrderFormAttachment[] {
  const uploads = assets
    .filter((asset) => asset.sectionKey === 'order-form' && asset.metadata?.orderFormId === form.id && asset.visibility !== 'hidden' && asset.metadata?.signed !== true && asset.metadata?.auditLog !== true)
    .map((asset) => ({ id: asset.id, title: asset.title, url: asset.url, kind: 'upload' as const, assetId: asset.id }))
  return [
    ...(form.includeDefaultCsa ? [{ id: 'default-csa', title: DEFAULT_CSA_TITLE, url: DEFAULT_CSA_URL, kind: 'default_csa' as const, assetId: null }] : []),
    ...uploads,
  ]
}

/**
 * The structured license terms and the PO number, as extra rows for the
 * Terms block of the document and the PDF. A row is left off when its value
 * is not set, so forms saved before these fields render as before.
 */
export function licenseTermRows(form: Pick<OrderForm, 'overageRate' | 'licensesPooled' | 'customer'>): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = []
  if (typeof form.overageRate === 'number' && Number.isFinite(form.overageRate) && form.overageRate > 0) rows.push({ label: 'Overage Rate', value: `${unitMoney(form.overageRate)} per License, billed monthly` })
  if (form.licensesPooled === true) rows.push({ label: 'Licenses', value: 'Pooled across the Subscription Period' })
  const po = (form.customer.poNumber || '').trim()
  if (po) rows.push({ label: 'PO Number', value: po })
  return rows
}

/** True when the form names a customer signer who is not the business contact. */
export function hasSeparateCustomerSigner(customer: Pick<OrderForm['customer'], 'signerName'>): boolean {
  return Boolean((customer.signerName || '').trim())
}

/**
 * The person who signs for the customer. The business contact signs unless
 * the form names a separate signer (signerName set). Older forms have no
 * signer fields, so their contact signs exactly as before.
 */
export function orderFormCustomerSigner(customer: OrderForm['customer']): { name: string; title: string; email: string } {
  if (hasSeparateCustomerSigner(customer)) return { name: (customer.signerName || '').trim(), title: (customer.signerTitle || '').trim(), email: (customer.signerEmail || '').trim() }
  return { name: customer.contactName.trim(), title: customer.contactTitle.trim(), email: customer.contactEmail.trim() }
}

/**
 * The billing contact as one line, "Name, email", or null when the form has
 * no separate billing contact (none set, or the signer's own email only).
 */
export function billingContactLine(customer: OrderForm['customer']): string | null {
  const name = (customer.billingName || '').trim()
  const email = customer.billingEmail.trim()
  const sameAsSigner = email.toLowerCase() === customer.contactEmail.trim().toLowerCase()
  if (!name && (!email || sameAsSigner)) return null
  return [name, email].filter(Boolean).join(', ')
}

// Customer edits (Rod, 2026-10-15): while a form is out for signature, the
// customer can correct their own address, billing contact and PO number.
// Nothing else. Pricing, lines, terms, dates and the signer stay with Prentus.
export const CUSTOMER_EDITABLE_FIELDS: readonly CustomerEditableField[] = ['address', 'billingName', 'billingEmail', 'poNumber']
export const CUSTOMER_EDIT_FIELD_LABEL: Record<CustomerEditableField, string> = {
  address: 'Company Address',
  billingName: 'Billing Contact Name',
  billingEmail: 'Billing Contact Email',
  poNumber: 'PO Number',
}
/** At most this many edits (one save is one edit) per form in 24 hours. */
export const CUSTOMER_EDIT_DAILY_LIMIT = 10

export type CustomerDetails = Record<CustomerEditableField, string>

export function customerDetails(form: Pick<OrderForm, 'customer'>): CustomerDetails {
  return { address: form.customer.address, billingName: form.customer.billingName || '', billingEmail: form.customer.billingEmail, poNumber: form.customer.poNumber || '' }
}

/** Why the customer cannot edit their details now, or null when they can. */
export function customerEditBlocked(form: Pick<OrderForm, 'status' | 'signing'>): string | null {
  if (form.status === 'signed') return 'This order form is already signed, so its details cannot change.'
  if (form.status !== 'sent' || !form.signing) return 'This order form is not out for signature.'
  if (form.signing.signers.some((signer) => signer.status === 'declined')) return 'This order form was declined. Contact Prentus to make changes.'
  const customer = form.signing.signers.find((signer) => signer.role === 'Customer')
  if (!customer || customer.status !== 'pending') return 'This order form is already signed, so its details cannot change.'
  return null
}

/** Why another edit cannot go in now (the daily limit), or null. Edits saved together count once. */
export function customerEditRateBlocked(form: Pick<OrderForm, 'customerEdits'>, now = Date.now()): string | null {
  const since = now - 24 * 60 * 60 * 1000
  const saves = new Set((form.customerEdits || []).filter((edit) => Date.parse(edit.at) > since).map((edit) => edit.at))
  return saves.size >= CUSTOMER_EDIT_DAILY_LIMIT ? 'You have changed these details several times today. Try again tomorrow, or contact Prentus.' : null
}

/**
 * Quiet lines for the admin Review & Send panel, newest first:
 * "Amanda updated the billing contact on Oct 16".
 */
export function customerEditLines(form: Pick<OrderForm, 'customerEdits' | 'customer'>, limit = 3): string[] {
  const groups = new Map<string, { by: string; fields: Set<CustomerEditableField> }>()
  for (const edit of form.customerEdits || []) {
    const group = groups.get(edit.at) || { by: edit.by, fields: new Set<CustomerEditableField>() }
    group.fields.add(edit.field)
    groups.set(edit.at, group)
  }
  const signer = orderFormCustomerSigner(form.customer)
  const signerEmail = signer.email.toLowerCase()
  return [...groups.entries()].sort(([a], [b]) => Date.parse(b) - Date.parse(a)).slice(0, limit).map(([at, group]) => {
    const who = group.by.trim().toLowerCase() === signerEmail && signer.name ? signer.name.split(/\s+/)[0] : group.by
    const parts = [
      ...(group.fields.has('address') ? ['the company address'] : []),
      ...(group.fields.has('billingName') || group.fields.has('billingEmail') ? ['the billing contact'] : []),
      ...(group.fields.has('poNumber') ? ['the PO number'] : []),
    ]
    const what = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0]
    const day = new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return `${who} updated ${what} on ${day}`
  })
}

// ---------------------------------------------------------------------------
// Order form activity (Rod, 2026-10-15): Prentus sees, and hears in Slack,
// when the customer opens the form, starts signing, downloads the PDF,
// changes their details, signs or declines.
// ---------------------------------------------------------------------------

export const ORDER_FORM_ACTIVITY_MAX = 50

/** Every event the Slack alert can describe: the stored activity types plus a customer edit. */
export type OrderFormAlertType = OrderFormActivityType | 'details_edited'

/** Signatures and declines alert every time. Other events alert once a day per person and form. */
export const ALWAYS_ALERT_ACTIVITY: ReadonlySet<OrderFormActivityType> = new Set(['signed', 'declined'])

/** The calendar day of a timestamp in Eastern time ("2026-10-16"): the day the daily alert limit counts. */
export function orderFormActivityDay(at: string): string {
  const date = new Date(at)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
}

export interface OrderFormActivityInput {
  id: string
  at: string
  type: OrderFormActivityType
  by: string
  name?: string | null
  detail?: string | null
}

/**
 * Adds one event to a form. A view, a start or a download that the same
 * person already did on the same form that day adds nothing; the existing
 * entry comes back with alert true only when its Slack alert has not gone
 * out yet (a failed alert is tried again). Signatures and declines are
 * always added and always alert. The history keeps the last 50 events.
 */
export function appendOrderFormActivity<T extends Pick<OrderForm, 'activity'>>(form: T, input: OrderFormActivityInput): { form: T; entry: OrderFormActivity; alert: boolean; added: boolean } {
  const by = input.by.trim().toLowerCase()
  const detail = input.detail || null
  const history = form.activity || []
  if (!ALWAYS_ALERT_ACTIVITY.has(input.type)) {
    const day = orderFormActivityDay(input.at)
    const existing = [...history].reverse().find((entry) => entry.type === input.type && entry.by === by && (entry.detail || null) === detail && orderFormActivityDay(entry.at) === day)
    if (existing) return { form, entry: existing, alert: !existing.alertedAt, added: false }
  }
  const entry: OrderFormActivity = { id: input.id, at: input.at, type: input.type, by, name: input.name?.trim() || null, detail, alertedAt: null }
  return { form: { ...form, activity: [...history, entry].slice(-ORDER_FORM_ACTIVITY_MAX) }, entry, alert: true, added: true }
}

/** Stamps the Slack alert on one entry, so the same day never alerts twice. */
export function markOrderFormActivityAlerted<T extends Pick<OrderForm, 'activity'>>(form: T, entryId: string, at: string): T {
  return { ...form, activity: (form.activity || []).map((entry) => entry.id === entryId ? { ...entry, alertedAt: at } : entry) }
}

const FIELD_PHRASE: Record<'address' | 'billing' | 'poNumber', string> = { address: 'the company address', billing: 'the billing contact', poNumber: 'the PO number' }

function editedParts(fields: Iterable<CustomerEditableField>): Array<keyof typeof FIELD_PHRASE> {
  const set = new Set(fields)
  return [
    ...(set.has('address') ? ['address' as const] : []),
    ...(set.has('billingName') || set.has('billingEmail') ? ['billing' as const] : []),
    ...(set.has('poNumber') ? ['poNumber' as const] : []),
  ]
}

function joinWithAnd(parts: string[]): string {
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] || ''
}

/**
 * The one-line Slack alert: "Amanda Soto opened the order form
 * OF-20261015-5K42 (University of Puerto Rico at Carolina)".
 */
export function orderFormActivityMessage(input: { type: OrderFormAlertType; actor: string; formNumber: string; accountName: string; detail?: string | null; fields?: CustomerEditableField[] }): string {
  const form = `the order form ${input.formNumber} (${input.accountName})`
  switch (input.type) {
    case 'viewed': return `${input.actor} opened ${form}`
    case 'signing_started': return `${input.actor} started signing ${form}`
    case 'pdf_downloaded': return input.detail === 'signed'
      ? `${input.actor} downloaded the signed order form ${input.formNumber} (${input.accountName})`
      : `${input.actor} downloaded the order form PDF ${input.formNumber} (${input.accountName})`
    case 'details_edited': {
      const what = joinWithAnd(editedParts(input.fields || []).map((part) => FIELD_PHRASE[part])) || 'their details'
      return `${input.actor} updated ${what} on ${form}`
    }
    case 'signed': return `${input.actor} signed ${form}`
    case 'declined': return `${input.actor} declined ${form}`
  }
}

const ACTIVITY_LABEL: Record<OrderFormActivityType, string> = {
  viewed: 'Viewed',
  signing_started: 'Started Signing',
  pdf_downloaded: 'Downloaded PDF',
  signed: 'Signed',
  declined: 'Declined',
}

const EDIT_LABEL: Record<keyof typeof FIELD_PHRASE, string> = { address: 'Company Address', billing: 'Billing Contact', poNumber: 'PO Number' }

export interface OrderFormTimelineItem { key: string; label: string; at: string; when: string; who: string | null }

/**
 * The admin Activity list, newest first: the recorded events plus the
 * customer's own edits. "who" is set only when someone other than the
 * customer signer did it.
 */
export function orderFormActivityTimeline(form: Pick<OrderForm, 'activity' | 'customerEdits' | 'customer'>, options: { limit?: number; timeZone?: string } = {}): OrderFormTimelineItem[] {
  const signer = orderFormCustomerSigner(form.customer).email.toLowerCase()
  const names = new Map<string, string>()
  for (const entry of form.activity || []) if (entry.name) names.set(entry.by, entry.name)
  const who = (email: string, name?: string | null): string | null => {
    const normalized = email.trim().toLowerCase()
    if (normalized === signer) return null
    return name || names.get(normalized) || normalized
  }
  const items: Array<Omit<OrderFormTimelineItem, 'when'>> = (form.activity || []).map((entry) => ({
    key: entry.id,
    label: entry.type === 'pdf_downloaded' && entry.detail === 'signed' ? 'Downloaded Signed Copy' : ACTIVITY_LABEL[entry.type],
    at: entry.at,
    who: who(entry.by, entry.name),
  }))
  const edits = new Map<string, { by: string; fields: CustomerEditableField[] }>()
  for (const edit of form.customerEdits || []) {
    const group = edits.get(edit.at) || { by: edit.by, fields: [] }
    group.fields.push(edit.field)
    edits.set(edit.at, group)
  }
  for (const [at, group] of edits) {
    items.push({ key: `edit:${at}`, label: `Updated ${joinWithAnd(editedParts(group.fields).map((part) => EDIT_LABEL[part]))}`, at, who: who(group.by) })
  }
  const format = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(options.timeZone ? { timeZone: options.timeZone } : {}) })
  return items
    .filter((item) => !Number.isNaN(Date.parse(item.at)))
    .sort((left, right) => Date.parse(right.at) - Date.parse(left.at))
    .slice(0, options.limit ?? 8)
    .map((item) => ({ ...item, when: format.format(new Date(item.at)) }))
}

/** The last time anyone on the customer side opened the form, or null. */
export function orderFormLastViewedAt(form: Pick<OrderForm, 'activity'>): string | null {
  const views = (form.activity || []).filter((entry) => entry.type === 'viewed').map((entry) => entry.at).sort()
  return views.length ? views[views.length - 1] : null
}

/** The signed contract and its signature certificate, once both parties have signed. */
export function signedDocuments(form: Pick<OrderForm, 'id' | 'status'>, assets: WorkspaceAsset[]): { signedCopy: WorkspaceAsset | null; certificate: WorkspaceAsset | null } {
  if (form.status !== 'signed') return { signedCopy: null, certificate: null }
  const mine = assets.filter((asset) => asset.sectionKey === 'order-form' && asset.metadata?.orderFormId === form.id && asset.visibility !== 'hidden')
  return { signedCopy: mine.find((asset) => asset.metadata?.signed === true) || null, certificate: mine.find((asset) => asset.metadata?.auditLog === true) || null }
}

const SIGNER_ROLE_PRIORITY = ['economic_buyer', 'decision_maker', 'procurement', 'champion', 'influencer', 'evaluator']

/**
 * Fills the company and both contacts from what the workspace already knows,
 * so converting a proposal does not mean retyping people. The customer
 * signer is the highest-priority stakeholder on the customer side with an
 * email; the Prentus contact is the first Prentus team member. Anything
 * already typed on the form is kept.
 */
export function prefillOrderFormParties(form: OrderForm, workspace: { accountName: string; allowedDomains?: string[]; members: WorkspaceMember[]; accountAddress?: string | null; orderForms?: OrderForm[] }, creator: AdminIdentity | null = null, signer: ContractSigner | null = null): OrderForm {
  const customers = workspace.members.filter((member) => member.side === 'customer' && member.email && !member.hidden)
  const customerSigner = [...customers].sort((a, b) => {
    const rank = (member: WorkspaceMember) => { const index = SIGNER_ROLE_PRIORITY.indexOf(member.stakeholderRole || ''); return index === -1 ? SIGNER_ROLE_PRIORITY.length : index }
    return rank(a) - rank(b)
  })[0] || null
  // Prepared by = whoever is building the form (it signs off the email and
  // gets the replies); without a session, the first Prentus team member.
  const creatorMember = creator ? workspace.members.find((member) => member.side === 'prentus' && member.email?.toLowerCase() === creator.email.toLowerCase()) : null
  const preparer = creator
    ? { name: creator.name, title: creator.title || creatorMember?.title || '', email: creator.email }
    : (() => { const member = workspace.members.find((entry) => entry.side === 'prentus' && entry.email && !entry.hidden); return member ? { name: member.name, title: member.title || '', email: member.email || '' } : null })()
  const website = form.customer.website || (workspace.allowedDomains || []).find((domain) => !/^(gmail|yahoo|outlook|hotmail)\./i.test(domain)) || ''
  const previousAddress = [...(workspace.orderForms || [])].filter((entry) => entry.id !== form.id && entry.customer.address.trim()).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]?.customer.address || ''
  const prepared = form.preparedBy || { name: '', title: '', email: '' }
  return {
    ...form,
    customer: {
      ...form.customer,
      legalName: form.customer.legalName || workspace.accountName,
      website,
      address: form.customer.address || workspace.accountAddress || previousAddress,
      contactName: form.customer.contactName || customerSigner?.name || '',
      contactTitle: form.customer.contactTitle || customerSigner?.title || '',
      contactEmail: form.customer.contactEmail || customerSigner?.email || '',
    },
    // The Prentus signer is a company setting, not a per-form choice.
    prentusContact: signer ? { ...signer } : form.prentusContact,
    preparedBy: {
      name: prepared.name || preparer?.name || '',
      title: prepared.title || preparer?.title || '',
      email: prepared.email || preparer?.email || '',
    },
  }
}
